import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';

const AuthContext = createContext();

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(null);

  useEffect(() => {
    // Check for stored token on app start
    const storedToken = Cookies.get('enakid_token');
    const storedUser = Cookies.get('enakid_user');
    
    if (storedToken && storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        setToken(storedToken);
        setUser(userData);
        // Set default Authorization header
        axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
      } catch (error) {
        console.error('Error parsing stored user data:', error);
        // Clear invalid stored data
        Cookies.remove('enakid_token');
        Cookies.remove('enakid_user');
      }
    }
    
    setLoading(false);
  }, []);

  // Auto-login function for testing
  const autoLogin = async () => {
    try {
      console.log('Attempting auto-login with demo credentials...');
      
      const formData = new FormData();
      formData.append('username', 'demo@enakid.test');
      formData.append('password', 'demo123456');

      const response = await axios.post(`${API}/auth/login`, formData, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });

      const { access_token, user: userData } = response.data;
      console.log('Auto-login successful!');
      
      // Store token and user data
      Cookies.set('enakid_token', access_token, { expires: 30 });
      Cookies.set('enakid_user', JSON.stringify(userData), { expires: 30 });
      
      setToken(access_token);
      setUser(userData);
      
      // Set default Authorization header
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      // Also ensure demo business plan exists
      await createDemoBusinessPlan(access_token);
      
    } catch (error) {
      console.error('Auto-login failed:', error);
      // Don't show error to user for auto-login failure
    }
  };

  // Create demo business plan if needed
  const createDemoBusinessPlan = async (token) => {
    try {
      console.log('Checking/creating demo business plan for TavPlans...');
      
      // Check if user already has business plans
      const plansResponse = await axios.get(`${API}/business-plans`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (plansResponse.data && plansResponse.data.length > 0) {
        console.log('Demo business plan already exists');
        return;
      }
      
      // Create demo business plan
      const planData = {
        name: "TavPlans Demo Business Plan",
        company_name: "TavPlans Demo Corp",
        business_type: "SaaS",
        industry: "Technology",
        country: "USA",
        currency: "USD"
      };
      
      const createResponse = await axios.post(`${API}/business-plans`, planData, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('Demo business plan created:', createResponse.data.id);
      
    } catch (error) {
      console.error('Failed to create demo business plan:', error);
    }
  };

  const login = async (email, password) => {
    try {
      console.log('AuthContext login called', { email, hasPassword: !!password, apiUrl: `${API}/auth/login` });
      
      const formData = new FormData();
      formData.append('username', email);
      formData.append('password', password);

      console.log('Making API request...');
      const response = await axios.post(`${API}/auth/login`, formData, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });
      console.log('Login response received:', response.status);

      // 2FA challenge — backend returns no tokens, requires TOTP verification
      if (response.data.requires_2fa) {
        return { success: false, requires2fa: true, email };
      }

      const { access_token, refresh_token, user: userData } = response.data;
      console.log('Login successful, token received:', !!access_token);
      
      // Store token and user data (access token expires in 1 hour, refresh in 7 days)
      Cookies.set('enakid_token', access_token, { expires: 1/24 }); // 1 hour
      Cookies.set('enakid_refresh_token', refresh_token, { expires: 7 }); // 7 days
      Cookies.set('enakid_user', JSON.stringify(userData), { expires: 7 });
      
      setToken(access_token);
      setUser(userData);
      
      // Set default Authorization header
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      return { success: true };
    } catch (error) {
      console.error('Login error:', error);
      console.error('Error response:', error.response?.data);
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Login failed' 
      };
    }
  };

  // Complete a 2FA-gated login by verifying the TOTP (or backup) code.
  const verify2FALogin = async (email, code) => {
    try {
      const response = await axios.post(`${API}/auth/2fa/verify-login`, { email, code });
      const { access_token, refresh_token, user: userData } = response.data;

      Cookies.set('enakid_token', access_token, { expires: 1 / 24 });
      Cookies.set('enakid_refresh_token', refresh_token, { expires: 7 });
      Cookies.set('enakid_user', JSON.stringify(userData), { expires: 7 });

      setToken(access_token);
      setUser(userData);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

      return { success: true, warning: response.data.warning };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || '2FA verification failed'
      };
    }
  };

  const register = async (userData) => {
    console.log('Email:', userData.email);
    console.log('API endpoint:', `${API}/auth/register`);
    try {
      const response = await axios.post(`${API}/auth/register`, userData, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      console.log('Registration successful:', response.status);
      
      const { access_token, refresh_token, user: registeredUser } = response.data;
      
      // Store token and user data
      Cookies.set('enakid_token', access_token, { expires: 1/24 }); // 1 hour
      Cookies.set('enakid_refresh_token', refresh_token, { expires: 7 }); // 7 days
      Cookies.set('enakid_user', JSON.stringify(registeredUser), { expires: 7 });
      
      setToken(access_token);
      setUser(registeredUser);
      
      // Set default Authorization header
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      return { success: true };
    } catch (error) {
      console.error('Registration error caught:', error);
      console.error('Error name:', error.name);
      console.error('Error message:', error.message);
      console.error('Error response:', error.response);
      console.error('Error response data:', error.response?.data);
      console.error('Error status:', error.response?.status);
      
      // Check if it's a network error
      if (!error.response) {
        console.error('Network error - no response received');
        return {
          success: false,
          error: 'Unable to connect to server. Please check your internet connection.'
        };
      }
      
      // Extract error message from response
      const errorMessage = 
        error.response?.data?.detail || 
        error.response?.data?.message || 
        error.response?.statusText ||
        'Registration failed';
      
      console.error('Final error message:', errorMessage);
      return { 
        success: false, 
        error: errorMessage
      };
    }
  };

  const logout = () => {
    // Clear stored data
    Cookies.remove('enakid_token');
    Cookies.remove('enakid_user');
    
    setToken(null);
    setUser(null);
    
    // Remove Authorization header
    delete axios.defaults.headers.common['Authorization'];
  };

  const updateProfile = async (profileData) => {
    try {
      const response = await axios.put(`${API}/auth/me`, profileData);
      
      const updatedUser = response.data;
      setUser(updatedUser);
      
      // Update stored user data
      Cookies.set('enakid_user', JSON.stringify(updatedUser), { expires: 30 });
      
      return { success: true };
    } catch (error) {
      console.error('Profile update error:', error);
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Profile update failed' 
      };
    }
  };

  const value = {
    user,
    token,
    loading,
    login,
    register,
    verify2FALogin,
    logout,
    updateProfile,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};